import {
  createQColumnHelper,
  QInheritedFromParentIcon,
  QSharedWithChildIcon,
  useFeatureFlag,
} from '@qualio/ui-components';
import { TagStatusMapping } from '@qualio/ui-components/lib/QOrganisms/Table/Cells/types';
import {
  QColumnOptions,
  QColumnWidth,
} from '@qualio/ui-components/lib/QOrganisms/Table/Columns';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentStatus, QualioDocument } from '../api/model/document';
import { getOwnerText } from '../util/UserUtils';
import { DocumentStatusIdToStatusConfigMap } from '../util/WorkspaceUtils';
import { OverviewRoutes } from '../views/components/DocumentOverview/routes';
import { useTags } from './useTags';

const columnHelper = createQColumnHelper<QualioDocument>();

export type DocumentColumnWidths = {
  code: QColumnWidth;
  title: QColumnWidth;
  type: QColumnWidth;
  owner: QColumnWidth;
  tags: QColumnWidth;
  version: QColumnWidth;
  status?: QColumnWidth;
};

export type DocumentTableColumnsConfig = {
  basePath: string;
  widths: DocumentColumnWidths;
  includeStatus?: boolean;
};

const statusMapping = Object.values(DocumentStatusIdToStatusConfigMap).reduce(
  (acc: TagStatusMapping, statusConfig) => {
    acc[statusConfig.label] = statusConfig.color;
    return acc;
  },
  {},
);

const getInheritedFromParentIcon = (document: QualioDocument) =>
  document.parent_company ? (
    <QInheritedFromParentIcon
      parentName={document.parent_company.name}
      data-cy={`inherited-document-icon-${document.id}`}
    />
  ) : null;

const getSharedWithIcon = (document: QualioDocument) => {
  const count = document.shared_company_ids?.length;
  if (document.parent_company || !count) {
    return null;
  }

  return (
    <QSharedWithChildIcon
      count={count}
      data-cy={`shared-with-icon-${document.id}`}
    />
  );
};

const getDocumentIcon = (document: QualioDocument) =>
  getInheritedFromParentIcon(document) ?? getSharedWithIcon(document);

export const appendDocumentIconProperties: Pick<
  QColumnOptions<QualioDocument>,
  'append' | 'appendPlacement'
> = {
  append: getDocumentIcon,
  appendPlacement: 'end',
};

export const useDocumentTableColumns = ({
  basePath,
  widths,
  includeStatus = false,
}: DocumentTableColumnsConfig) => {
  const { t } = useTranslation();
  const { data: tags = [] } = useTags();
  const { documentsTaskDrawer } = useFlags();
  const featureFlags = useFeatureFlag();
  const enterpriseAccounts = Boolean(featureFlags['enterprise-accounts']);

  return useMemo(() => {
    const columns = [
      columnHelper.id((document) => document.code, {
        header: t('tableHeaders.id'),
        id: 'code',
        width: widths.code,
      }),
      columnHelper.textLink(
        'title',
        (qualioDoc) =>
          documentsTaskDrawer
            ? `${basePath}/documents/${qualioDoc.id}/${OverviewRoutes.CONTENT}`
            : `${basePath}/documents/${qualioDoc.id}`,
        {
          header: t('tableHeaders.title'),
          id: 'title',
          width: widths.title,
          ...(enterpriseAccounts ? appendDocumentIconProperties : {}),
        },
      ),
      columnHelper.text((document) => document.type, {
        header: t('tableHeaders.type'),
        id: 'type',
        width: widths.type,
      }),
      columnHelper.text(getOwnerText, {
        header: t('tableHeaders.owner'),
        id: 'author_name',
        width: widths.owner,
      }),
      columnHelper.tag(
        (document) =>
          tags
            .filter((tag) => document.tag_ids.includes(tag.id))
            .map((tag) =>
              tag.group_ids.length
                ? { label: tag.name, leftIcon: 'Lock' as const }
                : tag.name,
            ),
        {
          statuses: { '*': 'default' },
          header: t('tableHeaders.tags'),
          enableSorting: false,
          width: widths.tags,
        },
      ),
      columnHelper.text(
        (document) => `${document.major_version}.${document.minor_version}`,
        {
          header: t('tableHeaders.version'),
          id: 'version',
          width: widths.version,
        },
      ),
    ];

    if (!includeStatus) {
      return columns;
    }

    return [
      ...columns,
      columnHelper.status(
        (document) =>
          DocumentStatusIdToStatusConfigMap[
            document.status_id as DocumentStatus
          ].label,
        {
          header: t('tableHeaders.status'),
          id: 'status_id',
          width: widths.status,
          enableSorting: true,
          statuses: statusMapping,
        },
      ),
    ];
  }, [
    tags,
    documentsTaskDrawer,
    enterpriseAccounts,
    t,
    basePath,
    widths,
    includeStatus,
  ]);
};
